<template>
  <div class="space-y-4">
    <!-- Search & Filters -->
    <div class="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
      <div class="flex flex-1 items-center gap-3 w-full sm:w-auto">
        <div class="relative flex-1 max-w-xs">
          <i class="fa fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
          <input
            v-model="localSearch"
            type="text"
            placeholder="Buscar por ref o empleado..."
            class="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            @input="onFilterChange"
          />
        </div>

        <select
          v-model="localEmployeeId"
          class="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
          @change="onFilterChange"
        >
          <option value="all">Todos los empleados</option>
          <option v-for="emp in allActiveEmployees" :key="emp.id" :value="emp.id">
            {{ emp.name }}
          </option>
        </select>
      </div>

      <div class="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end">
        <a
          :href="`/paysheet/export/excel?search=${localSearch}&employee_id=${localEmployeeId}`"
          class="px-3 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all shadow-2xs"
          title="Exportar a Excel para Finanzas con datos bancarios y montos en Bs"
          target="_blank"
        >
          <i class="fa fa-file-excel text-emerald-600 text-sm" />
          <span>Exportar Excel</span>
        </a>

        <a
          :href="`/paysheet/export/pdf?search=${localSearch}&employee_id=${localEmployeeId}`"
          class="px-3 py-2 bg-purple-50 border border-purple-200 text-purple-700 hover:bg-purple-100 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all shadow-2xs"
          title="Generar planilla consolidada en PDF para Finanzas"
          target="_blank"
        >
          <i class="fa fa-file-pdf text-purple-600 text-sm" />
          <span>Reporte Finanzas (PDF)</span>
        </a>

        <button
          type="button"
          class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm transition-all shrink-0 justify-center cursor-pointer active:scale-95"
          @click="$emit('open-settlement-modal')"
        >
          <i class="fa fa-hand-holding-usd" />
          <span>Nueva Liquidación</span>
        </button>
      </div>
    </div>

    <!-- Payments Table -->
    <div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse text-sm">
          <thead>
            <tr class="bg-gray-50/70 border-b border-gray-200/80 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
              <th class="py-3 px-4">Ref. Liquidación</th>
              <th class="py-3 px-4">Personal</th>
              <th class="py-3 px-4">Período de Corte</th>
              <th class="py-3 px-4">Turnos</th>
              <th class="py-3 px-4">Desglose</th>
              <th class="py-3 px-4">Total Pagado</th>
              <th class="py-3 px-4">Método & Ref</th>
              <th class="py-3 px-4">Gasto Asociado</th>
              <th class="py-3 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-if="!payments?.data || payments.data.length === 0">
              <td colspan="9" class="py-12 text-center text-gray-400">
                <i class="fa fa-receipt text-3xl mb-2 text-gray-300" />
                <p class="font-medium">No se han encontrado liquidaciones registradas.</p>
              </td>
            </tr>
            <tr 
              v-for="pay in payments?.data || []" 
              :key="pay.id"
              class="hover:bg-orange-50/20 transition-colors"
            >
              <!-- Reference -->
              <td class="py-3 px-4 font-mono font-bold text-gray-800 whitespace-nowrap">
                #{{ pay.reference_number }}
              </td>

              <!-- Employee -->
              <td class="py-3 px-4">
                <div class="font-bold text-gray-900">{{ pay.employee?.name }}</div>
                <div class="text-xs text-gray-500 flex items-center gap-1.5 flex-wrap">
                  <span>{{ pay.employee?.identification || 'Sin DNI' }}</span>
                  <span v-if="pay.employee?.bank_name" class="text-amber-700 font-semibold text-[11px] flex items-center gap-0.5">
                    • {{ pay.employee.bank_name }}
                  </span>
                </div>
              </td>

              <!-- Period -->
              <td class="py-3 px-4 text-xs text-gray-600 whitespace-nowrap">
                {{ formatDate(pay.start_date) }} al {{ formatDate(pay.end_date) }}
                <div class="text-[10px] text-gray-400 font-medium">Pago: {{ formatDate(pay.payment_date) }}</div>
              </td>

              <!-- Total Shifts -->
              <td class="py-3 px-4">
                <span class="px-2.5 py-1 rounded-lg text-xs font-bold bg-amber-50 text-amber-800">
                  {{ pay.total_shifts }} turnos
                </span>
              </td>

              <!-- Breakdown -->
              <td class="py-3 px-4 text-xs">
                <div>Base: <span class="font-bold text-gray-900">{{ formatUSD(pay.base_amount) }}</span></div>
                <div v-if="pay.bonus_amount > 0" class="text-emerald-600 font-semibold">+ Extras: {{ formatUSD(pay.bonus_amount) }}</div>
                <div v-if="pay.deduction_amount > 0" class="text-red-500 font-semibold">- Deduc: {{ formatUSD(pay.deduction_amount) }}</div>
              </td>

              <!-- Total Paid -->
              <td class="py-3 px-4">
                <div class="font-bold text-gray-900 text-base">{{ formatUSD(pay.total_paid) }}</div>
                <div class="text-[10px] text-gray-400">{{ formatBs(pay.total_paid) }}</div>
              </td>

              <!-- Method & Ref -->
              <td class="py-3 px-4 text-xs">
                <div class="font-semibold text-gray-800">{{ pay.payment_method }}</div>
                <div v-if="pay.payment_reference" class="font-mono text-gray-500 text-[11px]">
                  Ref: {{ pay.payment_reference }}
                </div>
              </td>

              <!-- Spent Link -->
              <td class="py-3 px-4">
                <Link
                  v-if="pay.spent_id"
                  :href="`/shoppings?tab=spents&search=${encodeURIComponent(pay.reference_number)}`"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors border border-teal-200"
                  title="Ver registro en Compras y Gastos (/shoppings)"
                >
                  <i class="fa fa-receipt text-[10px]" />
                  <span>Ver Gasto</span>
                </Link>
                <span v-else class="text-xs text-gray-400 italic">No vinculado</span>
              </td>

              <!-- Actions -->
              <td class="py-3 px-4 text-right">
                <div class="flex items-center justify-end gap-1.5">
                  <a
                    :href="`/paysheet/settlements/${pay.id}/receipt`"
                    target="_blank"
                    class="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Descargar Comprobante PDF"
                  >
                    <i class="fa fa-file-pdf text-sm" />
                  </a>

                  <button
                    type="button"
                    class="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    title="Anular Liquidación (revierte turnos a pendientes y borra el gasto)"
                    @click="$emit('delete-settlement', pay)"
                  >
                    <i class="fa fa-undo text-sm" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="payments?.links" class="p-4 border-t border-gray-100 flex justify-end">
        <Pagination :links="payments.links" :query-params="{ tab: 'payments', search: localSearch, employee_id: localEmployeeId }" />
      </div>
    </div>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Pagination from '@/Shared/Pagination'

export default {
  name: 'PaysheetPaymentsTab',

  components: {
    Link,
    Pagination,
  },

  props: {
    payments: {
      type: Object,
      required: true,
    },
    allActiveEmployees: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    currentExchangeRate: {
      type: Number,
      default: 1.0,
    },
  },

  emits: [
    'filter',
    'open-settlement-modal',
    'delete-settlement',
  ],

  data() {
    return {
      localSearch: this.filters?.search || '',
      localEmployeeId: this.filters?.employee_id || 'all',
    }
  },

  methods: {
    onFilterChange() {
      this.$emit('filter', {
        search: this.localSearch,
        employee_id: this.localEmployeeId,
      })
    },

    formatUSD(val) {
      const num = parseFloat(val) || 0
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    formatBs(val) {
      const num = (parseFloat(val) || 0) * (this.currentExchangeRate || 1)
      return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Bs.'
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const parts = dateStr.split('T')[0].split('-')
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`
      }
      return dateStr
    },
  },
}
</script>
